import { useEffect, useMemo, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, useUpdateQueryParams, withStyles } from 'vopay';
import { BodyText, Button, Card, DropdownMenu, Title } from 'UI';
import { useModuleFeatureFlag } from '@/providers/FeatureFlag.provider';
import { usePartnerData } from '@/providers/PartnerData.provider';
import { usePermissions } from '@/providers/Permissions.provider';
import { ACCOUNTS_PATH } from '@/components/Entities/paths';
import { PARTNER_MANAGEMENT_PATH } from '@/components/Partners/PartnerManagement/paths';
import canCreatePartners from '@/components/Partners/PartnerManagement/canCreatePartners.helper';
import OrganizationChartPanel, { OrganizationChartPanelHandle } from './OrganizationChartPanel';
import OrganizationOverviewFilters from './OrganizationOverviewFilters';
import OrganizationSummaryCards from './OrganizationSummaryCards';
import useLoadOrganization from './useLoadOrganization';
import { CHART_VIEW_SEARCH } from './useChartView';
import {
  DEFAULT_ORGANIZATION_SHOW_FILTER,
  isOrganizationShowFilter,
  isOrganizationStatusFilter,
  NO_ORGANIZATION_FILTER,
  OrganizationFilter,
} from './organizationTree';
import styles from './OrganizationOverviewPage.module.scss';

const cn = withStyles(styles);

type QueryParams = {
  /** Empty for the default (partners and direct accounts). */
  show: string;
  /** Empty for every status. */
  status: string;
};

/**
 * The partner's whole organization as a tree (spec §6): the organization at the top, then its
 * partners, accounts and sub-accounts. The Show and Status filters live in the address so a link
 * reopens the same view. Each card creates the entity's children on hover (6.6) and tracks its
 * onboarding (6.8).
 */
export default function OrganizationOverviewPage() {
  const { t } = useTranslation('translations', { keyPrefix: 'Entities.OrganizationOverviewPage' });
  const history = useHistory();
  const location = useLocation();
  const updateQueryParams = useUpdateQueryParams<QueryParams>();

  const { permissions } = usePermissions();

  const { canCreateAccount, isEnabled: canViewAccounts } = useModuleFeatureFlag('accounts');
  const { isEnabled: canViewPartners } = useModuleFeatureFlag('partners');

  const {
    response: { isRoleAdmin, partnerType },
  } = usePartnerData();

  // The endpoint only lists child partners when the partner may create them, so without that there
  // are no partner branches to show, hide or add to.
  const hasPartnerBranches = permissions.PartnerCreationEnabled;

  // The Partner Accounts page's own rule for its Add Partner button.
  const canCreatePartner = canCreatePartners({
    isRoleAdmin,
    isPartnerCreationEnabled: hasPartnerBranches,
    partnerType,
  });

  // Creating an account from this page, header or card, is an Admin's action, whatever the Accounts
  // page itself offers a Support user.
  const canAddAccount = isRoleAdmin && canCreateAccount;

  const { organization, isLoadingOrganization, loadOrganization } = useLoadOrganization();

  // On mount only: the hook recreates its load function every render, so depending on it would
  // refetch forever (the Accounts list fetches its status summary the same way).
  useEffect(() => {
    loadOrganization();
  }, []);

  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  // An unknown value in the address falls back to the default rather than hiding everything.
  const show = useMemo(() => {
    const raw = queryParams.get('show');

    return hasPartnerBranches && isOrganizationShowFilter(raw) ? raw : DEFAULT_ORGANIZATION_SHOW_FILTER;
  }, [queryParams, hasPartnerBranches]);

  // Same for a status the control never offers: it would match nothing, with no option to say why.
  const status = useMemo(() => {
    const raw = queryParams.get('status') ?? '';

    return isOrganizationStatusFilter(raw) ? raw : '';
  }, [queryParams]);

  const filter = useMemo<OrganizationFilter>(() => ({ ...NO_ORGANIZATION_FILTER, status }), [status]);

  // The header's Add Entity opens the chart's own creation drawers.
  const chartPanel = useRef<OrganizationChartPanelHandle>(null);

  return (
    <Box flx="col" gapSize="24px">
      <Box jc="sb" ai="fs">
        {/* Figma: H1/Large Title 32/Emphasized + Body/16/Regular (Text/Secondary75) */}
        <div className={cn('pageHeading')}>
          <Title>{t('title', { defaultValue: 'Organization Overview' })}</Title>

          <BodyText className={cn('pageDescription')}>
            {t('subtitle', { defaultValue: 'Your whole organization at a glance.' })}
          </BodyText>
        </div>

        {/* Figma `Buttons`: Type="Primary"; Size="Large"; Left Indicator="add"; Right Indicator="expand_more" */}
        <div className={cn('headerActions')}>
          {canCreatePartner && canAddAccount ? (
            <DropdownMenu>
              <DropdownMenu.Trigger asChild>
                <Button
                  id="organization-addEntity"
                  colorScheme="primary"
                  size="large"
                  iconLeft="add"
                  iconRight="expand_more"
                >
                  {t('addEntityButton', { defaultValue: 'Add Entity' })}
                </Button>
              </DropdownMenu.Trigger>

              <DropdownMenu.Content align="end">
                <DropdownMenu.Item onSelect={() => chartPanel.current?.createPartner()}>
                  {t('addPartnerOption', { defaultValue: 'Partner' })}
                </DropdownMenu.Item>

                <DropdownMenu.Item onSelect={() => chartPanel.current?.createAccount()}>
                  {t('addAccountOption', { defaultValue: 'Account' })}
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu>
          ) : canAddAccount ? (
            <Button
              id="organization-addAccount"
              colorScheme="primary"
              size="large"
              iconLeft="add"
              onClick={() => chartPanel.current?.createAccount()}
            >
              {t('addAccountButton', { defaultValue: 'Add Account' })}
            </Button>
          ) : (
            canCreatePartner && (
              <Button
                id="organization-addPartner"
                colorScheme="primary"
                size="large"
                iconLeft="add"
                onClick={() => chartPanel.current?.createPartner()}
              >
                {t('addPartnerButton', { defaultValue: 'Add Partner' })}
              </Button>
            )
          )}
        </div>
      </Box>

      <OrganizationSummaryCards
        fetching={isLoadingOrganization}
        totals={organization?.Totals}
        onViewPartners={
          canViewPartners && canCreatePartner
            ? () => history.push(`${PARTNER_MANAGEMENT_PATH}${CHART_VIEW_SEARCH}`)
            : undefined
        }
        onViewAccounts={canViewAccounts ? () => history.push(`${ACCOUNTS_PATH}${CHART_VIEW_SEARCH}`) : undefined}
      />

      <Card variant="section" className={cn('chartCard')}>
        <OrganizationChartPanel
          ref={chartPanel}
          organization={organization}
          isLoading={isLoadingOrganization}
          show={show}
          filter={filter}
          filters={
            <OrganizationOverviewFilters
              show={show}
              onShowChange={(next) =>
                updateQueryParams({ show: next === DEFAULT_ORGANIZATION_SHOW_FILTER ? '' : next })
              }
              hasShowFilter={hasPartnerBranches}
              status={status}
              onStatusChange={(next) => updateQueryParams({ status: next })}
            />
          }
          onOrganizationChanged={loadOrganization}
        />
      </Card>
    </Box>
  );
}
